/**
 * The `class` values a projection breakdown's elements may carry. Most of them — this list — go on a
 * `<p>` and name the chart bar that paragraph is about; the two below the list are the exceptions.
 *
 * The engine's per-year breakdown (see `ProjectionYearResult.breakdown`) is plain semantic HTML that
 * any surface can render. These tags are the one concession to the projections modal, where the
 * breakdown sits under an accordion of charts: they let it mark a paragraph's margin with the colour
 * of the bar the sentence moves — the family home's growth in the home colour, a HELOC drawdown in
 * the care-debt colour — with no extra elements and nothing for a plain renderer to trip over.
 *
 * Each tag is `<chart>-<series id>`, matching a series in the modal's charts. The mapping to actual
 * colours lives with those charts (frontend/src/features/agedCare/projectionBreakdownStyles.ts), and
 * a test there asserts every tag here still resolves to one — so a renamed series fails loudly rather
 * than silently dropping a paragraph's colour.
 *
 * Paragraphs with no single matching bar (aged care costs, living expenses, whole-of-balance-sheet
 * statements) carry no class at all, and their margin is deliberately left empty.
 */
export const BREAKDOWN_SERIES_TAGS = [
    // Cashflow chart — income the year received.
    'cashflow-salary',
    'cashflow-pension',
    'cashflow-other',
    // Net Worth chart — what is owned.
    'networth-home',
    'networth-cash',
    'networth-shares',
    'networth-super',
    'networth-rad',
    // Debt chart — what is owed.
    'debt-reverseMortgage',
    'debt-heas',
    'debt-heloc',
] as const;

export type BreakdownSeriesTag = (typeof BREAKDOWN_SERIES_TAGS)[number];

/**
 * The one tag above that names no series: a birthday. It marks its margin too, but with a glyph rather
 * than a colour, because the sentence marks an occasion rather than moving a bar on any chart — there
 * is no series for it to borrow a colour from. Kept beside the others so the engine that emits it and
 * the stylesheet that draws it name it once.
 */
export const BREAKDOWN_BIRTHDAY_TAG = 'birthday';

/**
 * The `<div>` the engine wraps each MONTH of a breakdown in, so the year reads as bands rather than as
 * one unbroken column of dates: the modal alternates their backgrounds and stands each month's `<h2>`
 * name up in a rail down the band's left edge. A plain renderer sees a div with a heading in it.
 */
export const BREAKDOWN_MONTH_CLASS = 'breakdown-month';
